import {
	CircularProgress,
	Grid2,
	IconButton,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import {
	ChatBotBubbleIcon,
	ChatConsultant,
	DownloadIcon,
} from '../assets/icons/Icons';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';
import { isBlobUrl } from '../Utils/Helpers';

interface ChatDataProps {
	position: string;
	text: string;
	chatTime: string;
	fileName?: string;
	fileUrl?: string;
	onDownload?: (url: string) => void;
	caption?: string;
	chatOwner: string;
	isDownloading?: boolean;
	downloadingFile?: string;
	isThisReplyMsg?: boolean;
	repliedMsg?: string;
	isMsgHasReaction?: boolean;
	reaction?: string;
	isFileIsImage?: boolean;
	imageURL?: string;
}

function ChatBubble(data: ChatDataProps) {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	function getChatBubbleStyle() {
		if (data.position === 'left') {
			return {
				borderTopRightRadius: '24px',
				borderBottomLeftRadius: '24px',
				borderBottomRightRadius: '24px',
			};
		} else {
			return {
				borderTopLeftRadius: '24px',
				borderBottomLeftRadius: '24px',
				borderBottomRightRadius: '24px',
			};
		}
	}

	function isMessageIsTooLong() {
		const textLength = data.text.length;
		if (textLength > 40) {
			return true;
		} else {
			return false;
		}
	}

	function getChatOwnerTag(type: string) {
		if (type === 'consultant') {
			return 'Consultant';
		} else {
			return 'AI Assistant';
		}
	}

	function getChatIcon(type: string) {
		if (type === 'consultant') {
			return <ChatConsultant />;
		} else if (type === 'ai') {
			return <ChatBotBubbleIcon />;
		} else {
			return <></>;
		}
	}

	function getBubbleWidth() {
		return !isMobile
			? isMessageIsTooLong() || data.isThisReplyMsg || data.isFileIsImage
				? '40%'
				: null
			: null;
	}

	return (
		<Grid2
			component={'section'}
			sx={{
				display: 'flex',
				alignItems: {
					xs: data.position === 'right' ? 'flex-end' : 'flex-start',
					sm: 'flex-start',
				},
				justifyContent: {
					xs: 'flex-start',
					sm: data.position === 'right' ? 'flex-end' : 'flex-start',
				},
				gap: '3px',
				flexDirection: { xs: 'column-reverse', sm: 'row' },
				width: '100%',
				minWidth: 0,
			}}>
			<Grid2
				component={'section'}
				sx={{
					position: 'relative',
					maxWidth: getBubbleWidth(),
					...getChatBubbleStyle(),
					display: 'inline-block',
					minWidth: 0,
					wordBreak: 'break-word',
					backgroundColor:
						data.position === 'left' ? '#F2F4F5' : MAIN_THEME_COLOR,
					paddingX: '20px',
					paddingY: '10px',
					marginTop: {
						xs: '0px',
						sm: data.position === 'right' ? '15px' : '0px',
					},
				}}>
				{data.position === 'right' && (
					<Typography
						variant="body2"
						textAlign={'left'}
						color={'#E2FAFB'}
						fontWeight={'700'}
						paddingY={'5px'}>
						{getChatOwnerTag(data.chatOwner)}
					</Typography>
				)}
				{data.isThisReplyMsg && (
					<Grid2
						sx={{
							backgroundColor:
								data.position === 'left'
									? '#E3E8EB'
									: '#555555',
							borderRadius: 1,
							px: 1.5,
							py: 1,
							mb: 1,
							boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.2)',
							justifyContent: 'center',
							borderLeft: `5px solid ${data.position === 'left' ? MAIN_THEME_COLOR : '#F2F4F5'}`,
						}}>
						{isBlobUrl(data.repliedMsg || '') ? (
							<img
								src={data.repliedMsg}
								alt="Media"
								style={{
									maxWidth: '100%',
									maxHeight: '120px',
									width: 'auto',
									height: 'auto',
									borderRadius: 8,
									display: 'block',
								}}
							/>
						) : (
							<Typography
								style={{
									overflowWrap: 'break-word',
									whiteSpace: 'pre-line',
									fontSize: '11px',
								}}
								color={
									data.position === 'left'
										? '#000000'
										: '#FFFFFF'
								}>
								{data.repliedMsg}
							</Typography>
						)}
					</Grid2>
				)}
				{data.fileName && data.fileUrl ? (
					<Grid2 container flexDirection={'column'}>
						{data.isFileIsImage && (
							<img
								src={data.imageURL}
								alt="Media"
								style={{
									maxWidth: '100%',
									maxHeight: '250px',
									width: 'auto',
									height: 'auto',
									borderRadius: 8,
									display: 'block',
								}}
							/>
						)}
						<Grid2
							container
							flexDirection={'row'}
							width={'100%'}
							alignItems={'center'}
							sx={{
								backgroundColor:
									data.position === 'left'
										? '#F0F0F0'
										: MAIN_THEME_COLOR,
								padding: '5px',
								borderRadius: '8px',
								boxShadow: !data.isFileIsImage
									? '0 2px 2px rgba(0, 0, 0, 0.1)'
									: '',
							}}>
							<Typography
								variant="body2"
								style={{
									overflowWrap: 'break-word',
									maxWidth: isMobile
										? 'calc(90% - 10px)'
										: 'calc(98% - 10px)',
								}}
								color={
									data.position === 'left'
										? '#000000'
										: '#FFFFFF'
								}>
								{!data.isFileIsImage && data.fileName}
							</Typography>
							{data.isDownloading &&
							data.downloadingFile === data.fileUrl ? (
								<CircularProgress
									sx={{
										color:
											data.position === 'left'
												? '#28696D'
												: '#FFFFFF',
										marginLeft: '5px',
										padding: '2px',
									}}
									size={24}
								/>
							) : (
								<IconButton
									sx={{
										marginLeft: '5px',
										padding: '2px',
									}}
									onClick={() =>
										data.onDownload &&
										data.onDownload(
											data.fileUrl ? data.fileUrl : '',
										)
									}>
									<DownloadIcon
										color={
											data.position === 'left'
												? MAIN_THEME_COLOR
												: '#FFFFFF'
										}
									/>
								</IconButton>
							)}
						</Grid2>
						<Typography
							variant="caption"
							sx={{
								color:
									data.position === 'left'
										? '#8E8E8E'
										: '#B0B0B0',
								maxWidth: '100%',
								overflowWrap: 'break-word',
								paddingTop: '5px',
								paddingLeft: '5px',
							}}>
							{data.caption}
						</Typography>
					</Grid2>
				) : (
					<Typography
						variant="body2"
						style={{
							overflowWrap: 'break-word',
							whiteSpace: 'pre-line',
						}}
						color={
							data.position === 'left' ? '#000000' : '#FFFFFF'
						}>
						{data.text}
					</Typography>
				)}
				<Typography
					fontSize={'8px'}
					textAlign={data.position === 'left' ? 'start' : 'end'}
					color={data.position === 'left' ? '#000000' : '#FFFFFF'}
					paddingTop={'5px'}>
					{data.chatTime}
				</Typography>
				{data.isMsgHasReaction && (
					<Grid2
						sx={{
							position: 'absolute',
							backgroundColor:
								data.position === 'left'
									? MAIN_THEME_COLOR
									: '#F2F4F5',
							bottom: '-18px',
							[data.position === 'left' ? 'left' : 'right']:
								'20px',
							display: 'flex',
							gap: '10px',
							alignItems: 'center',
							borderRadius: '50%',
							width: '28px',
							height: '28px',
							padding: '2px',
							justifyContent: 'center',
						}}>
						<Typography
							fontSize={'12px'}
							textAlign={'center'}
							color={
								data.position === 'left' ? '#000000' : '#FFFFFF'
							}>
							{data.reaction}
						</Typography>
					</Grid2>
				)}
			</Grid2>
			{data.position === 'right' && getChatIcon(data.chatOwner)}
		</Grid2>
	);
}

function ChatRow(chat: ChatDataProps) {
	return (
		<Grid2
			container
			justifyContent={
				chat.position === 'right' ? 'flex-end' : 'flex-start'
			}
			padding={'10px'}>
			<ChatBubble
				position={chat.position}
				text={chat.text}
				chatTime={chat.chatTime}
				fileName={chat.fileName}
				fileUrl={chat.fileUrl}
				onDownload={chat.onDownload}
				caption={chat.caption}
				chatOwner={chat.chatOwner}
				isDownloading={chat.isDownloading}
				downloadingFile={chat.downloadingFile}
				isThisReplyMsg={chat.isThisReplyMsg}
				repliedMsg={chat.repliedMsg}
				isMsgHasReaction={chat.isMsgHasReaction}
				reaction={chat.reaction}
				isFileIsImage={chat.isFileIsImage}
				imageURL={chat.imageURL}
			/>
		</Grid2>
	);
}

export default ChatRow;
